import { type ChatMessage, MAX_CHAT_MESSAGE_LENGTH } from "@loot-os/api-contract";
import { IconButton } from "@loot-os/design-system/core/IconButton";
import { ChatComposer } from "@loot-os/design-system/domain/chat/ChatComposer";
import { ChatRulesModal } from "@loot-os/design-system/domain/chat/ChatRulesModal";
import eyeOffIcon from "@loot-os/icons/icons/eye-off";
import rulesIcon from "@loot-os/icons/icons/rules";
import { useNavigate } from "@tanstack/react-router";
import { type Ref, useState } from "react";

import {
  muteUntilLabel,
  toUserFacingChatError,
  useChatRulesAgreed,
  useSendChatMessage,
} from "../../lib/chat.js";
import { useShopper } from "../../lib/shopper.js";
import { UsernameDialog } from "../UsernameDialog.js";

/** Boxed's chat rules (`CHAT_RULES`), with the brand taken out. */
const CHAT_RULES = [
  { title: "Be respectful", description: "Treat everyone with kindness and courtesy." },
  {
    title: "No spam or excessive messages",
    description: "Keep the chat clean and avoid flooding with repetitive content.",
  },
  {
    title: "No offensive language",
    description: "Avoid using profanity, hate speech, or discriminatory language.",
  },
  {
    title: "No self-promotion",
    description:
      "Refrain from promoting personal channels, websites, or services. Links are not allowed.",
  },
  { title: "Stay on topic", description: "Keep discussions relevant to the community and site." },
];

/**
 * The message box behind boxed's gates, one at a time and in order: signed in, the session ready,
 * a username (the name everyone sees), the rules agreed, not muted. Enter sends, Shift+Enter
 * breaks the line. Nothing shows optimistically: the text clears on send and stays when the
 * server refuses it, with the reason above the field. The rules and the ignored players open from
 * the tools under it.
 */
export function StoreChatComposer({
  roomId,
  username,
  profileLoaded,
  muted,
  mutedUntil,
  replyingTo,
  onSent,
  actionError,
  rulesOpen,
  onRulesOpenChange,
  onOpenIgnored,
  inputRef,
}: {
  roomId: string;
  username: string | null;
  /** The viewer's profile (and so their username) has loaded. */
  profileLoaded: boolean;
  muted: boolean;
  mutedUntil: string | undefined;
  replyingTo: ChatMessage | null;
  onSent: () => void;
  /** Why a message action (a delete) failed. */
  actionError: string | undefined;
  rulesOpen: boolean;
  onRulesOpenChange: (open: boolean) => void;
  onOpenIgnored: () => void;
  /** The message box, so a reply can move focus into it. */
  inputRef?: Ref<HTMLTextAreaElement>;
}) {
  const navigate = useNavigate();
  const { user, sessionToken, authPending, sessionPending } = useShopper();
  const send = useSendChatMessage(roomId);
  const [rulesAgreed, agreeToRules] = useChatRulesAgreed();
  const [draft, setDraft] = useState("");
  const [sendError, setSendError] = useState<string | undefined>(undefined);
  const [usernameOpen, setUsernameOpen] = useState(false);
  // The rules opened from the gate ask to agree; opened from the tools, they are a plain read.
  const [agreeing, setAgreeing] = useState(false);

  const sessionReady = !authPending && !sessionPending && !!sessionToken && profileLoaded;
  const until = muteUntilLabel(mutedUntil);
  const gate = !user
    ? {
        message: "Sign in to chat.",
        action: { label: "Sign in", onClick: () => void navigate({ to: "/login" }) },
      }
    : !sessionReady
      ? { message: "Connecting…" }
      : !username
        ? {
            message: "Other players see your username in chat.",
            action: { label: "Choose a username", onClick: () => setUsernameOpen(true) },
          }
        : !rulesAgreed
          ? {
              message: "Agree to the rules to chat.",
              action: {
                label: "Read the rules",
                onClick: () => {
                  setAgreeing(true);
                  onRulesOpenChange(true);
                },
              },
            }
          : muted
            ? {
                message: until ? `You are muted until ${until}.` : "You have been muted in chat.",
              }
            : undefined;

  const onSend = async (text: string) => {
    setSendError(undefined);
    try {
      await send.mutateAsync({
        text,
        ...(replyingTo ? { replyToMessageId: replyingTo.messageId } : {}),
      });
      onSent();
    } catch (err) {
      setSendError(toUserFacingChatError(err));
      // Thrown, the composer keeps the draft to send again.
      throw err;
    }
    return undefined;
  };

  return (
    <>
      <ChatComposer
        label="Chat message"
        inputRef={inputRef}
        placeholder="Type something..."
        sendLabel="Send"
        value={draft}
        onValueChange={setDraft}
        maxLength={MAX_CHAT_MESSAGE_LENGTH}
        mode={gate ? undefined : `${draft.length}/${MAX_CHAT_MESSAGE_LENGTH}`}
        onSend={onSend}
        sending={send.isPending}
        error={sendError ?? actionError}
        gate={gate}
        tools={
          <>
            <IconButton
              icon={rulesIcon}
              label="Chat rules"
              size="sm"
              onClick={() => {
                setAgreeing(false);
                onRulesOpenChange(true);
              }}
            />
            <IconButton
              icon={eyeOffIcon}
              label="Ignored players"
              size="sm"
              onClick={onOpenIgnored}
            />
          </>
        }
      />
      <ChatRulesModal
        open={rulesOpen}
        onOpenChange={onRulesOpenChange}
        title="Chat rules"
        intro="Staff remove messages and mute players who break them."
        rules={CHAT_RULES}
        {...(agreeing
          ? {
              agreeLabel: "I agree",
              dismissLabel: "Cancel",
              onAgree: () => {
                agreeToRules();
                onRulesOpenChange(false);
              },
            }
          : { dismissLabel: "Got it" })}
      />
      {usernameOpen && (
        <UsernameDialog open={usernameOpen} onOpenChange={setUsernameOpen} username={null} />
      )}
    </>
  );
}
